/* Nolae — Nolae Family (/pages/nolae-family). Built by
   nolae-family-theme/scripts/build_js.py from the prototype's public/
   scripts; do not edit by hand. */

/* ————— smooth.js ————— */
/* Nolae — smooth scrolling (Lenis).
 *
 * Vendored rather than CDN-linked so the theme serves it from its own assets.
 * The Liquid equivalent of what layout.tsx does here is three tags in
 * theme.liquid, in this order:
 *
 *   {{ 'lenis.css'     | asset_url | stylesheet_tag }}
 *   {{ 'lenis.min.js'  | asset_url | script_tag }}
 *   {{ 'smooth.js'     | asset_url | script_tag }}
 *
 * lenis.min.js is a plain IIFE that sets globalThis.Lenis — no module loader, no
 * dependencies, 5.4KB gzipped — which is the whole reason this is shippable as a
 * theme asset at all.
 *
 * WHY THIS DOES NOT BREAK THE REST OF THE PAGE
 * Lenis is left in its default window mode, where it animates the real document
 * scroll position via scrollTo(). That matters more than it sounds: the page
 * leans on `position: sticky` for the story stage and the header, and both
 * glass.js and story-scroll.js derive their state from getBoundingClientRect()
 * and native scroll events. A transform-based smooth-scroll wrapper — the other
 * common approach — would silently break every one of those, because sticky
 * stops working inside a transformed ancestor. Real scroll keeps all of it
 * truthful, and the native scroll event still fires, so nothing downstream needs
 * to know Lenis is here.
 */
(function () {
  "use strict";

  // Someone who has asked for less motion did not ask for scroll inertia. Bail
  // before construction so the page keeps plain native scrolling.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  if (typeof window.Lenis !== "function") return;

  // Not in Safari (this page only, 2026-10-12 — see CLAUDE.md). Safari runs
  // requestAnimationFrame at ~60Hz on 120Hz ProMotion displays by default,
  // and Lenis drives the scroll from rAF — so in Safari the page scrolled at
  // half the display's rate and read as jarring, while Chrome (120Hz rAF)
  // was smooth. Safari's own trackpad/wheel scrolling is already smooth and
  // runs at the display's rate, so it keeps that. Chrome, Edge and Firefox
  // (and every iOS browser, which is touch anyway) are unaffected.
  var ua = navigator.userAgent;
  if (/Safari\//.test(ua) && !/Chrome\/|Chromium\/|CriOS\/|FxiOS\/|Edg\/|EdgiOS\/|Android/.test(ua)) return;

  var lenis = new window.Lenis({
    // Frame-rate independent smoothing. Lower is slower to settle; 0.09 lands
    // just short of floaty, which suits a shop — the scroll still feels like it
    // answers to the wheel rather than coasting past where you aimed.
    lerp: 0.09,
    wheelMultiplier: 1,
    // Wheel and trackpad only. Touch keeps the platform's own momentum: phones
    // already scroll well, and syncing touch here fights the OS and costs more
    // than it adds.
    syncTouch: false,
    // Lenis runs its own rAF loop, so there is no second loop to keep alive.
    autoRaf: true,
    // In-page links still need to work — this keeps them, smoothed.
    anchors: true,
  });

  /* NO scroll-event bridge here, deliberately — and it is worth recording why,
   * because it looks like an obvious safety net.
   *
   * glass.js and story-scroll.js listen for `scroll` on window, and re-emitting
   * Lenis' own event as a native one to guarantee they hear it causes infinite
   * recursion: Lenis itself LISTENS to native scroll, to notice scrolling it did
   * not cause. It flags its own writes (_preventNextNativeScrollEvent) and
   * ignores those, but a synthetic event carries no such flag, so Lenis treats it
   * as external and emits again — straight into a "Maximum call stack size
   * exceeded" that takes the whole page's scroll logic down with it.
   *
   * No bridge is needed anyway: in default mode Lenis animates the real document
   * scroll position, so the browser fires native scroll events on its own. That
   * same fact is what keeps `position: sticky` working here.
   */

  // Exposed for console poking and so a section script could pause scrolling
  // (lenis.stop() / lenis.start()) if a modal ever needs it.
  window.__lenis = lenis;
})();

/* ————— fam-chrome.js ————— */
/* Nolae Family — the chrome. A copy of faq-theme/src/faq-chrome.js (itself
 * about-us-theme/src/au-chrome.js).
 *
 * What the prototype's public/glass.js did for its own stand-in header, done
 * for the theme's real one — the same approach as the Idol A-Z, Release
 * Calendar and About Us ports:
 *
 *   • the header slides away on a sustained scroll down and comes back on a
 *     longer scroll up, armed only once the hero has scrolled under it;
 *   • --chrome-top tells everything pinned below where to park, which is what
 *     the sticky topic list parks under;
 *   • data-chrome-hold (set by faq.js for the length of a topic jump) keeps
 *     the header away so the jump lands where it was aimed, as glass.js did;
 *   • the theme's header-color-adapt is neutralised (see below).
 *
 * The frosted band is NOT here. The prototype drew its own (body::before, fed
 * by glass.js) because its stand-in header had none; the live theme already
 * runs that exact mechanism — it is the one the prototype's was ported from —
 * and the only chrome this page pins is the header, which is what the theme
 * sizes the band to.
 */
(function () {
  "use strict";
  var root = document.documentElement;
  var header = document.querySelector('.shopify-section--header, [id^="shopify-section"][id$="__header"]');
  var page = document.querySelector(".nolae-fam");
  if (!page) return;

  var HERO_ENTRY = 0.2;
  var DELTA = 2, HIDE_AFTER = 24, REVEAL_AFTER = 120;
  var up = false, lastY = window.scrollY, run = 0;

  function headerSticky() {
    if (!header) return false;
    var p = getComputedStyle(header).position;
    return p === "sticky" || p === "fixed";
  }

  /* Where the bottom of the pinned chrome is. The story stage pins against
     this, so it has to be published on every scroll, not only when the header
     moves — the header's own position drifts while it is sliding. */
  var lastTop = -1;
  function publishTop() {
    var top = 0;
    if (header && !up && headerSticky()) {
      top = Math.max(0, header.getBoundingClientRect().top) + header.offsetHeight;
    }
    top = Math.round(top);
    if (top !== lastTop) {
      root.style.setProperty("--chrome-top", top + "px");
      lastTop = top;
    }
  }

  /* Armed once the header's bottom edge reaches the hero band's first fifth. */
  function pastHeroEntry() {
    var hero = page.querySelector(".banner");
    if (!hero || !header) return true;
    var r = hero.getBoundingClientRect();
    return r.top + r.height * HERO_ENTRY <= header.offsetHeight;
  }

  function setUp(v) {
    // Never while a theme drawer is open — the cart and menu slide over the page.
    if (v && document.body.classList.contains("lock-all")) v = false;
    if (v === up) return;
    up = v;
    root.classList.toggle("nolae-chrome-up", v);
    publishTop();
    /* The header takes 0.32s to travel. Everything that measures against it —
       the sticky topic list, the current-topic tracking — hears only scroll events, so give
       them three across the transition rather than leaving them a frame
       behind. The prototype's glass.js does the same, for the same reason. */
    [80, 200, 340].forEach(function (ms) {
      setTimeout(function () { window.dispatchEvent(new Event("scroll")); }, ms);
    });
  }

  function autoHide() {
    if (!header) return;
    var y = window.scrollY;
    if (root.hasAttribute("data-chrome-hold")) { lastY = y; run = 0; setUp(true); return; }
    var diff = y - lastY;
    if (Math.abs(diff) < DELTA) return;        // ignore sub-pixel jitter
    lastY = y;
    if (diff > 0 !== run > 0) run = 0;         // direction changed: start a new run
    run += diff;
    if (!pastHeroEntry()) { run = 0; setUp(false); return; }
    if (run >= HIDE_AFTER) setUp(true);
    else if (run <= -REVEAL_AFTER) setUp(false);
  }

  /* ————— Header link colours —————
     The theme's header-color-adapt section, once the header is `scrolled`,
     samples what is behind each nav link and forces it white over a dark patch.
     On this page that misfires the same way it did on the Release Calendar: the
     hero's dark scrim reads as dark, but the header has its own light surface,
     so the links go white on near-white — and the script bails as soon as the
     header un-scrolls without undoing it. Here the header only ever sits over
     the hero or the page's own white, so the theme's own colours are always
     right: whatever the adapter writes is taken straight back off.
     MutationObserver callbacks run before paint, so the white never shows. */
  var adapted = document.querySelectorAll(".header__linklist-link, .header__secondary-links .header__icon-wrapper svg");
  function unadapt(el) { if (el.style.getPropertyValue("color")) el.style.removeProperty("color"); }
  if (adapted.length && window.MutationObserver) {
    var mo = new MutationObserver(function (records) {
      records.forEach(function (r) { unadapt(r.target); });
    });
    [].forEach.call(adapted, function (el) {
      unadapt(el);
      mo.observe(el, { attributes: true, attributeFilter: ["style"] });
    });
  }

  function onScroll() { autoHide(); publishTop(); }
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", publishTop);
  if (window.ResizeObserver && header) new ResizeObserver(publishTop).observe(header);
  publishTop();
})();

/* ————— blog-split.js ————— */
(function () {
  function go(fontsOk) {
    var r = document.querySelector('.nolae-fam'); if (r) r.classList.add('fam-split-go');
/* Nolae Blog — per-line split for the section headings' entrance.
 *
 * Ported from the About Us prototype's public/2/hero-split.js. The measuring
 * and rebuilding below are that file's, unchanged; what differs is how groups
 * are chosen. About Us names a fixed list of selectors because it has one of
 * each heading; this page has five section heads, so each [data-split] element
 * is its own group and its lines count 0,1,2… from their own start.
 *
 * ORIGINAL HEADER FOLLOWS
 * ───────────────────────
 * Nolae About Us — per-line split for the hero's entrance.
 *
 * The reference (sendpotion.com) staggers its headline one LINE at a time, each
 * line swinging up out of a backward tilt. Lines are not addressable in CSS, so
 * they have to be made into elements — and where a line breaks depends on the
 * width and on which font is loaded, so it cannot be written into the markup
 * either. This measures the real breaks and wraps them, then re-measures whenever
 * they could have changed.
 *
 * Method: wrap every word in a span, read each one's top, group the spans that
 * share a top into one block per line — then throw the word spans away again,
 * collapsing each line back to plain text. That last step matters for screen
 * readers: a paragraph split into one element per word is read badly by some, and
 * once the lines are known the words have served their purpose. What is left is
 * three blocks of ordinary text.
 *
 * Non-text children survive as single tokens: the inline gif keeps its place
 * mid-sentence, and "K-pop" keeps the nowrap span that stops it breaking at its
 * hyphen.
 *
 * Four groups use this: the hero, which plays on load, and "Meet the team",
 * "Culture at Nolae" and "Content House of K-pop", which each hold until the
 * reader reaches them. The
 * splitting is identical either way — WHEN the lines animate is a CSS question,
 * keyed off a class the relevant section's own script adds.
 *
 * If this never runs, the CSS leaves the hero as plain static text — the entrance
 * is additive, never a precondition for reading the page.
 *
 * Deliberately framework-free (IIFE, no imports, no build step) so it drops into a
 * Shopify Liquid section unchanged:
 *   {{ 'blog-split.js' | asset_url | script_tag }}
 */
(function () {
  "use strict";

  /* Groups, not one flat list. Each group restarts the stagger index at 0, so
     the team's two blocks count 0,1,2... of their own rather than continuing
     from the hero's — sharing a counter would have handed the team's first line
     a delay several hundred ms long before it even started.
     Within a group the index DOES carry across elements, which is what makes
     the hero's eyebrow and headline read as one run of lines. */
  /* One group per section head. Inside a group the index carries across the
     heading and its blurb, so the two read as one run of lines — exactly as
     [".cult-title", ".cult-blurb"] does on About Us. */
  var GROUP_ROOT = "[data-split]";
  // Nolae Family adds the collaboration steps' title + text and the
  // Collaborations head (deliberate
  // divergence from ../idol-a-z's copy).
  var IN_GROUP = ".row-title, .row-desc, .banner-title, .banner-desc, .fam-step-t, .fam-step-d, .fam-head .eyebrow, .fam-head .fam-h2, .fam-head .fam-lead, .fam-uwu-head .eyebrow, .fam-uwu-head .fam-h2, .fam-uwu-head .fam-lead";
  var lastWidth = -1;

  function tokenise(el) {
    // Words become spans; elements pass through whole.
    var tokens = [];
    var nodes = [].slice.call(el.childNodes);
    el.textContent = "";
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.nodeType === 3) {
        var parts = node.nodeValue.split(/([^\S\u00a0\u202f\u2007]+)/);
        for (var j = 0; j < parts.length; j++) {
          if (!parts[j]) continue;
          if (/^[^\S\u00a0\u202f\u2007]+$/.test(parts[j])) {
            // Real whitespace, so the browser still has its break opportunities.
            el.appendChild(document.createTextNode(parts[j]));
          } else {
            var w = document.createElement("span");
            w.textContent = parts[j];
            el.appendChild(w);
            tokens.push(w);
          }
        }
      } else if (node.nodeType === 1) {
        el.appendChild(node);
        tokens.push(node);
      }
    }
    return tokens;
  }

  function split(el, startIndex) {
    /* A re-split (resize, font arrival) of a heading whose entrance has
       already played must not play it again: the rebuilt lines are new
       elements and would restart the keyframe from invisible. */
    var again = el.__orig != null && !!el.closest(".is-arrived");
    if (el.__orig == null) el.__orig = el.innerHTML;
    // Hidden across the rebuild so the un-split text is never painted; both
    // happen in one task, so there is no flash either way.
    el.style.visibility = "hidden";
    el.classList.remove("is-split");
    el.innerHTML = el.__orig;

    var tokens = tokenise(el);
    if (!tokens.length) { el.style.visibility = ""; return startIndex; }

    /* Group into lines by each token's vertical CENTRE, against half a line-height
       of tolerance.
       Not by rect top with a small tolerance, which is the obvious version and is
       wrong here: tokens on one line do not share a top. A word's box is its
       glyphs, the inline gif's is 75px tall and vertically centred, and the two
       differ by enough to look like separate lines — which split the sentence's
       middle line into three and stretched the headline from 234 to 390.
       Centres of tokens on one line agree closely, while the next line's are a
       full line-height away, so half a line-height separates them cleanly whatever
       is sitting in the text. */
    var lh = parseFloat(getComputedStyle(el).lineHeight);
    if (!lh || isNaN(lh)) lh = parseFloat(getComputedStyle(el).fontSize) * 1.3 || 20;
    var tol = lh * 0.5;

    var lines = [];
    var lineCentre = null;
    for (var i = 0; i < tokens.length; i++) {
      var box = tokens[i].getBoundingClientRect();
      /* A token that is not rendered has no position to group by, and its zero
         rect sits nowhere near the real lines — so it would start a line of its
         own. That is not hypothetical: the inline gif is display:none at the
         mobile breakpoint, where it split the middle of the sentence in three and
         left the headline a line taller than it should be. Carry such tokens along
         with whatever line is open instead. */
      if (!box.width && !box.height) {
        if (!lines.length) lines.push([]);
        lines[lines.length - 1].push(tokens[i]);
        continue;
      }
      var centre = box.top + box.height / 2;
      if (lineCentre === null || Math.abs(centre - lineCentre) > tol) {
        lines.push([]);
        lineCentre = centre;
      }
      lines[lines.length - 1].push(tokens[i]);
    }

    // Rebuild as one block per line, collapsing the word spans back to text.
    var frag = document.createDocumentFragment();
    for (var l = 0; l < lines.length; l++) {
      var line = document.createElement("span");
      line.className = "hero-line";
      line.style.setProperty("--i", String(startIndex + l));
      if (again) line.style.animation = "none";
      var group = lines[l];
      for (var t = 0; t < group.length; t++) {
        var tok = group[t];
        /* A <br> did its job during measurement — it is why this group is a
           separate line at all. Carrying it into the rebuilt line would add an
           empty line box below the text and inflate the block's height. */
        if (tok.tagName === "BR") continue;
        var isWord = tok.tagName === "SPAN" && !tok.className;
        if (isWord) {
          var prev = line.lastChild;
          var text = tok.textContent;
          // Put the separating space back between words on the same line.
          if (t > 0) text = " " + text;
          if (prev && prev.nodeType === 3) prev.nodeValue += text;
          else line.appendChild(document.createTextNode(text));
        } else {
          if (t > 0) line.appendChild(document.createTextNode(" "));
          line.appendChild(tok);
        }
      }
      frag.appendChild(line);
    }
    el.textContent = "";
    el.appendChild(frag);

    el.style.visibility = "";
    el.classList.add("is-split");
    return startIndex + lines.length;
  }

  /* `force` re-splits groups already done (line breaks may have moved);
     without it only groups not yet split are touched — which is what lets
     this run twice: once mid-parse for the category rows, and again at
     DOMContentLoaded for the "All articles" head that arrives after them.
     See the note at the top of blog-reveal.js for why the first pass exists. */
  function run(force) {
    var groups = document.querySelectorAll(GROUP_ROOT);
    for (var g = 0; g < groups.length; g++) {
      if (groups[g].__split && !force) continue;
      groups[g].__split = true;
      var index = 0;
      var els = groups[g].querySelectorAll(IN_GROUP);
      for (var i = 0; i < els.length; i++) index = split(els[i], index);
    }
    lastWidth = window.innerWidth;
  }

  function onResize() {
    // Only width can change where a line breaks; height cannot, and on mobile it
    // changes constantly as the address bar comes and goes.
    if (window.innerWidth === lastWidth) return;
    run(true);
  }

  var fontsLoaded = fontsOk || !!(document.fonts && document.fonts.status === "loaded");

  function init() {
    run();
    window.addEventListener("resize", onResize);
    // The fallback face breaks lines somewhere else, so re-split once Omnes
    // lands — unless it already had when the first split was measured.
    if (document.fonts && document.fonts.ready && !fontsLoaded) {
      document.fonts.ready.then(function () { run(true); });
    }
  }

  // First pass right now, mid-parse, for whatever heads already exist; the
  // second at DOMContentLoaded for the rest.
  init();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { run(); });
  }
})();

  }
  var f = document.fonts, done = false;
  function once(ok) { if (!done) { done = true; go(ok === true); } }
  if (f && f.load) {
    setTimeout(once, 800);
    Promise.all([f.load('600 1em "Omnes Blog"'), f.load('500 1em "Omnes Blog"'), f.load('400 1em "Source Sans Pro"')]).then(function () { once(true); }, once);
  } else once();
})();

/* ————— blog-reveal.js ————— */
/* Scroll reveal for the blog index.
 *
 * Vanilla and standalone so it ports to the theme unchanged, as
 * {{ 'blog-reveal.js' | asset_url | script_tag }} — no imports, no build step.
 * Everything carrying [data-reveal] starts hidden in CSS and is released once,
 * the first time it comes near the viewport.
 */
/* Two forms of the attribute, as on About Us /2:
 *
 *   data-reveal=""            fade this element up when it arrives (`is-in`)
 *   data-reveal="<selector>"  add `is-arrived` to this SECTION once the element
 *                             the selector picks comes into view
 *
 * The second is what the split headings key off: the lines live inside the
 * head, but the class that releases them has to land on an ancestor, so the
 * section carries it and names its own trigger.
 */
/* WHEN this runs matters more than what it does. The page is large — every
 * article is in the markup — and the CSS hides each [data-reveal=""] card from
 * the first paint until this script releases it. Loaded at the end of the
 * document, it would not run until the whole thing had parsed, and Safari in
 * particular showed the section titles seconds before their cards. So the
 * page includes it as a plain blocking <script> straight after the category
 * rows: it executes mid-parse, the moment those rows exist, and the cards in
 * view are released at once. Anything parsed later (the "All articles" head)
 * is picked up by the second pass at DOMContentLoaded — scan() only ever
 * observes an element once, so running it twice is safe.
 */
(function () {
  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var hasIO = typeof IntersectionObserver === "function";
  var release = function (section) { section.classList.add("is-arrived"); };

  var aio = hasIO && new IntersectionObserver(
    function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        release(e.target.__section || e.target);
        aio.unobserve(e.target);
      });
    },
    // The same depth the fade below trips at, so every entrance on the page
    // arrives at the same point in the scroll.
    { rootMargin: "0px 0px -8% 0px" }
  );

  var io = hasIO && new IntersectionObserver(
    function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add("is-in");
        io.unobserve(e.target); // one-way: nothing re-hides on the way back up
      });
    },
    /* Fires a little before the element arrives, so the motion has finished by
       the time it is properly in view rather than starting there. */
    { rootMargin: "0px 0px -8% 0px", threshold: 0.05 }
  );

  function scan() {
    var anchored = document.querySelectorAll("[data-reveal]:not([data-reveal=''])");
    for (var a = 0; a < anchored.length; a++) {
      var section = anchored[a];
      if (section.__seen) continue;
      section.__seen = true;
      var anchor = section.querySelector(section.getAttribute("data-reveal"));
      if (!anchor || !hasIO) { release(section); continue; }
      anchor.__section = section;
      aio.observe(anchor);
    }

    var items = document.querySelectorAll("[data-reveal='']");
    for (var i = 0; i < items.length; i++) {
      var el = items[i];
      if (el.__seen) continue;
      el.__seen = true;
      /* Reduced motion: show immediately and never observe. The CSS already
         neutralises the transition, but leaving items hidden until an observer
         fires would be worse than not animating at all. */
      if (reduced || !hasIO) el.classList.add("is-in");
      else io.observe(el);
    }
  }

  scan();
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", scan);
})();

/* ————— count-up.js ————— */
/* Fast count-up for the "Find the perfect fit" tile's numbers (726, 8.3K, 349).
 *
 * Each [data-count] inside a [data-count-group] runs from 0 to its value,
 * shown with its own data-decimals and data-suffix (8.3K: 1 and "K"),
 * once the group scrolls into view — about 2s, ease-out, so it starts quick
 * and settles on the figure. The markup already holds the final value, so
 * without JS (or with reduced motion) the numbers simply show as they are.
 *
 * Vanilla and standalone, like the other scripts in public/.
 */
(function () {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  if (typeof IntersectionObserver !== "function") return;

  var DURATION = 2000; // ms
  var fmt = function (el, v) {
    return v.toFixed(parseInt(el.getAttribute("data-decimals") || "0", 10)) + (el.getAttribute("data-suffix") || "");
  };
  var ease = function (t) { return 1 - Math.pow(1 - t, 3); };

  function run(group) {
    var nums = group.querySelectorAll("[data-count]");
    var start = null;
    function frame(now) {
      if (start === null) start = now;
      var t = Math.min(1, (now - start) / DURATION);
      for (var i = 0; i < nums.length; i++) {
        nums[i].textContent = fmt(nums[i], parseFloat(nums[i].getAttribute("data-count")) * ease(t));
      }
      if (t < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      run(e.target);
    });
  }, { threshold: 0.5 });

  /* The referral tile's chart: [data-draw] gets .is-armed (plot hidden) right
     away and .is-drawn once it is half on screen — page.css wipes it in. */
  var drawIo = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      drawIo.unobserve(e.target);
      e.target.classList.add("is-drawn");
    });
  }, { threshold: 0.5 });

  function init() {
    var draws = document.querySelectorAll("[data-draw]");
    for (var d = 0; d < draws.length; d++) {
      draws[d].classList.add("is-armed");
      drawIo.observe(draws[d]);
    }
    var groups = document.querySelectorAll("[data-count-group]");
    for (var g = 0; g < groups.length; g++) {
      var nums = groups[g].querySelectorAll("[data-count]");
      if (!nums.length) continue;
      for (var i = 0; i < nums.length; i++) nums[i].textContent = fmt(nums[i], 0);
      io.observe(groups[g]);
    }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();

/* ————— chips-drop.js ————— */
/* Nolae Family — the job-role chips' entrance, after ../about-us
 * public/team-drop.js ("Meet the team").
 *
 * The chips start as a heap in the middle of their card and spread out to
 * their places the first time the card is reached, once per page load.
 * CSS does all the moving (.fam-crew.is-piled / .is-spread in page.css);
 * this file only decides WHEN, and measures WHERE the heap is.
 *
 * Unlike About Us, the heap is measured rather than written in frame units:
 * on a phone the chips flow (wrapped, centred) instead of sitting at frame
 * coordinates, so each chip's way to the heap — --tx/--ty — is the vector
 * from its resting centre to the card's centre plus its own nudge (--px/--py,
 * the About Us pile offsets, scaled to the card). Re-measured on resize until
 * the spread has happened.
 *
 * No IntersectionObserver or reduced motion: no heap, chips simply in place.
 * Framework-free IIFE so it drops into a Liquid section unchanged.
 */
(function () {
  "use strict";

  /* ————— Mobile bands' scroll progress —————
     --tp on .fam-chips: 0 as the card's top reaches the bottom of the
     viewport, 1 as its bottom leaves the top — the bands slide exactly while
     the card is on screen (About Us team-drop.js's tp()). Written on the chip
     list, not the card, so only the tracks restyle each frame. Desktop
     ignores it. Runs whatever the motion setting: under reduced motion the
     bands still follow the scroll the reader is doing themselves, as on
     About Us. */
  function bands(card) {
    var list = card.querySelector(".fam-chips");
    if (!list) return;
    /* Phones only: the bands exist only there, and on desktop rewriting
       --tp every scroll frame restyled 42 chips for nothing (scroll jank,
       user, 2026-10-12). Checked per event so a resize across 768 works. */
    var phone = window.matchMedia("(max-width: 768px)");
    var ticking = false, last = -1;
    function tp() {
      ticking = false;
      var r = card.getBoundingClientRect();
      var span = r.height + window.innerHeight;
      var p = (window.innerHeight - r.top) / span;
      p = p < 0 ? 0 : p > 1 ? 1 : p;
      if (Math.abs(p - last) < 0.0005) return;
      last = p;
      list.style.setProperty("--tp", p.toFixed(4));
    }
    function on() {
      if (ticking || !phone.matches) return;
      ticking = true;
      requestAnimationFrame(tp);
    }
    if (phone.matches) tp();
    window.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on, { passive: true });
  }

  function init() {
    var card = document.querySelector(".fam-crew");
    if (!card) return;
    bands(card);
    if (!("IntersectionObserver" in window) ||
        (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches)) {
      return;
    }

    /* The loop's copies (.is-dup) are display:none on desktop and never
       piled on mobile, so only the real chips are measured. */
    var chips = card.querySelectorAll(".fam-chip-slot:not(.is-dup) .fam-chip");

    function measure() {
      if (card.classList.contains("is-spread")) return;
      var cr = card.getBoundingClientRect();
      if (!cr.width) return;
      /* The heap sits in the space the chips occupy — above the apply card's
         overlap, so the middle of the chip list, not of the whole card. */
      var list = card.querySelector(".fam-chips").getBoundingClientRect();
      var cx = list.left + list.width / 2;
      var cy = list.top + list.height / 2;
      /* About Us nudges are in its frame units; scale them with the card. */
      var s = cr.width / 970;
      for (var i = 0; i < chips.length; i++) {
        var slot = chips[i].parentElement;
        var r = slot.getBoundingClientRect();
        var st = getComputedStyle(slot);
        var px = parseFloat(st.getPropertyValue("--px")) || 0;
        var py = parseFloat(st.getPropertyValue("--py")) || 0;
        chips[i].style.setProperty("--tx", (cx + px * s - (r.left + r.width / 2)).toFixed(1) + "px");
        chips[i].style.setProperty("--ty", (cy + py * s - (r.top + r.height / 2)).toFixed(1) + "px");
      }
    }

    /* Slots don't move when .is-piled lands (only the chips inside them do),
       so measuring before and after is the same. */
    measure();
    card.classList.add("is-piled");

    function spread() {
      card.classList.add("is-spread");
    }

    /* Arrived = the card's middle has risen past 80% of the viewport. A
       "has passed" test, like About Us's pileArrived(): it stays true for any
       downward journey however fast, and works for a card taller than the
       screen — where an IntersectionObserver ratio threshold never fires.
       Checked on load (deep link, refresh partway down), on scroll, and from
       an observer as a second route in case scroll events are coalesced. */
    function arrived() {
      var r = card.getBoundingClientRect();
      return r.height > 0 && r.top + r.height / 2 <= window.innerHeight * 0.8;
    }
    var ticking = false;
    function check() {
      ticking = false;
      if (card.classList.contains("is-spread")) return;
      if (arrived()) {
        spread();
        window.removeEventListener("scroll", onScroll);
        io.disconnect();
      }
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(check);
    }
    var io = new IntersectionObserver(check, { threshold: [0, 0.25, 0.5, 0.75, 1] });
    io.observe(card);
    window.addEventListener("scroll", onScroll, { passive: true });
    /* Two frames so the heap is painted before it starts to open. */
    if (arrived()) requestAnimationFrame(function () { requestAnimationFrame(check); });

    window.addEventListener("resize", measure, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/* ————— anim-offscreen.js ————— */
/* Nolae Family — pause always-on animations while they are off screen.
 *
 * The loyalty share tile (28 flying emojis, 4 bolts) animates forever in
 * pure CSS. Running them while
 * nobody can see them only gives scrolling something to compete with, so
 * every box marked [data-live] gets .is-off while it is outside the viewport
 * and page.css pauses its animations (animation-play-state). One observer,
 * no per-frame work; without IntersectionObserver nothing is ever paused.
 *
 * Framework-free IIFE so it drops into a Liquid section unchanged.
 */
(function () {
  "use strict";
  function init() {
    if (!("IntersectionObserver" in window)) return;
    var boxes = document.querySelectorAll("[data-live]");
    if (!boxes.length) return;
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        entries[i].target.classList.toggle("is-off", !entries[i].isIntersecting);
      }
    }, { rootMargin: "100px 0px" });
    for (var i = 0; i < boxes.length; i++) {
      boxes[i].classList.add("is-off"); // until the observer reports in
      io.observe(boxes[i]);
    }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
